import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getServiceBySlug, services } from '../data/services';
import { testimonials } from '../data/testimonials';
import PageSEO from '../components/PageSEO';

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  const [formData, setFormData] = useState({ prenom: '', nom: '', email: '', telephone: '', codePostal: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await fetch('/api/contact-rapide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, typeProjet: service?.slug }),
      });
    } catch (_) {}
    setSubmitted(true);
    setSubmitting(false);
  };

  if (!service) {
    return (
      <div className="py-32 text-center">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">Prestation introuvable</h2>
        <Link to="/services" className="text-orange-600 font-semibold hover:underline">← Retour aux services</Link>
      </div>
    );
  }

  const related = services.filter((s) => s.slug !== slug && s.category === service.category).slice(0, 3);
  const serviceTestimonials = testimonials.slice(0, 2);

  return (
    <>
      <PageSEO
        title={`${service.name} à Châteauroux — Indre (36)`}
        path={`/services/${service.slug}`}
        description={`${service.shortDesc} Intervention dans tout le Berry (Indre 36, Cher 18). Garantie décennale. Devis gratuit.`}
      />
      {/* Hero */}
      <section className="bg-slate-900 py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-sm text-slate-400 mb-4 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-orange-400">Accueil</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-orange-400">Services</Link>
            <span>/</span>
            <span className="text-white">{service.name}</span>
          </div>
          <div className="flex items-center gap-4 mb-4">
            <span className="text-5xl">{service.icon}</span>
            <div>
              <h1 className="text-4xl font-extrabold text-white">{service.name}</h1>
              <p className="text-orange-400 font-semibold mt-1">À partir de {service.price}€ · {service.duration}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-3 lg:gap-10">
            {/* Main content */}
            <div className="lg:col-span-2 mb-10 lg:mb-0">
              <img src={service.image} alt={service.name} className="w-full h-72 object-cover rounded-2xl mb-8" />
              <h2 className="text-2xl font-bold text-slate-900 mb-4">À propos de cette prestation</h2>
              <p className="text-slate-600 leading-relaxed mb-8 text-base">{service.fullDesc}</p>

              <h3 className="text-xl font-bold text-slate-900 mb-4">Ce que comprend notre prestation</h3>
              <ul className="space-y-3 mb-8">
                {service.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <span className="w-5 h-5 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center text-xs shrink-0 mt-0.5">✓</span>
                    <span className="text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-orange-50 border border-orange-200 rounded-xl p-6 mb-8">
                <h4 className="font-bold text-orange-800 mb-2">💡 Le saviez-vous ?</h4>
                <p className="text-orange-700 text-sm leading-relaxed">
                  Tous nos travaux sont couverts par notre garantie décennale. Nous vous accompagnons de A à Z dans votre projet de rénovation.
                </p>
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-5">Notre processus d'intervention</h3>
              <div className="space-y-4">
                {['Visite technique et diagnostic gratuit', 'Devis détaillé sous 48h', 'Planification des travaux', 'Réalisation par nos artisans certifiés', 'Réception des travaux et garantie'].map((step, i) => (
                  <div key={step} className="flex items-center gap-4 bg-white rounded-xl p-4 border border-slate-200">
                    <div className="w-9 h-9 bg-orange-600 text-white rounded-lg flex items-center justify-center font-bold text-sm shrink-0">{i + 1}</div>
                    <span className="text-slate-700 font-medium">{step}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sticky form */}
            <div>
              <div className="sticky top-28 bg-white rounded-2xl shadow-xl border border-slate-200 p-7">
                {submitted ? (
                  <div className="text-center py-6">
                    <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-emerald-600 text-2xl">✓</span>
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">Demande envoyée !</h3>
                    <p className="text-slate-500 text-sm">Notre équipe vous contacte sous 24h pour organiser une visite gratuite.</p>
                  </div>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-slate-900 mb-1">Devis pour "{service.name}"</h3>
                    <p className="text-slate-500 text-sm mb-5">Gratuit · Sans engagement · Réponse sous 24h</p>
                    <form className="space-y-4" onSubmit={handleSubmit}>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Votre nom</label>
                        <input type="text" required className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="Jean Dupont"
                          value={formData.nom} onChange={e => setFormData({ ...formData, nom: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Adresse email</label>
                        <input type="email" required className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="votre@email.fr"
                          value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Téléphone</label>
                        <input type="tel" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="06 XX XX XX XX"
                          value={formData.telephone} onChange={e => setFormData({ ...formData, telephone: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Code postal</label>
                        <input type="text" className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500" placeholder="36000"
                          value={formData.codePostal} onChange={e => setFormData({ ...formData, codePostal: e.target.value })} />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">Décrivez votre projet</label>
                        <textarea rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none" placeholder="Surface, état actuel, vos souhaits..."
                          value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} />
                      </div>
                      <button type="submit" disabled={submitting}
                        className="w-full bg-orange-600 hover:bg-orange-700 disabled:bg-orange-400 text-white font-bold py-3.5 rounded-lg transition-colors">
                        {submitting ? 'Envoi…' : 'Obtenir mon devis →'}
                      </button>
                    </form>
                    <div className="mt-4 space-y-2 text-xs text-slate-500">
                      {/* <p>✅ Certifié RGE — Éligible aides État</p> à rétablir après obtention qualif */}
                      <p>✅ Artisans qualifiés</p>
                      <p>🛡️ Garantie décennale incluse</p>
                      <p>🔒 Données sécurisées</p>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-8">Ce que disent nos clients</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {serviceTestimonials.map((t) => (
              <div key={t.id} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <div className="flex mb-3">
                  {'★★★★★'.split('').map((_, i) => <span key={i} className={`text-lg ${i < t.rating ? 'text-yellow-400' : 'text-slate-200'}`}>★</span>)}
                </div>
                <p className="text-slate-600 italic text-sm leading-relaxed mb-4">"{t.text}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-orange-600 flex items-center justify-center text-white text-xs font-bold">{t.avatar}</div>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                    <p className="text-slate-400 text-xs">{t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="py-16 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-slate-900 mb-8">Autres prestations qui pourraient vous intéresser</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {related.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="bg-white rounded-xl p-5 border border-slate-200 hover:border-orange-300 hover:shadow-md transition-all group flex items-start gap-4">
                  <span className="text-3xl">{s.icon}</span>
                  <div>
                    <h4 className="font-bold text-slate-900 group-hover:text-orange-600 transition-colors">{s.name}</h4>
                    <p className="text-slate-500 text-sm mt-1">À partir de {s.price}€</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
